import { motion, AnimatePresence } from "motion/react";
import { X, CheckCircle, AlertCircle, Package, Calendar, MapPin } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";

interface MaterialDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  material: {
    id: string;
    name: string;
    quality: string;
    compliance: "compliant" | "non-compliant";
    imageUrl: string;
    specifications?: {
      grade?: string;
      source?: string;
      batchNumber?: string;
      testDate?: string;
      quantity?: string;
      supplier?: string;
    };
  } | null;
}

export default function MaterialDetailModal({
  isOpen,
  onClose,
  material,
}: MaterialDetailModalProps) {
  if (!material) return null;

  const specs = material.specifications || {};

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Bottom Sheet Modal */}
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center pointer-events-none pb-16 md:pb-0">
            <motion.div
              initial={{ opacity: 0, y: "100%" }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: "100%" }}
              transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
              className="bg-white rounded-t-3xl md:rounded-2xl w-full md:max-w-lg max-h-[85vh] md:max-h-[85vh] overflow-hidden pointer-events-auto"
              style={{ boxShadow: "0 -10px 40px rgba(0, 0, 0, 0.15), 0 20px 50px rgba(0, 0, 0, 0.3)" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag Handle (Mobile Only) */}
              <div className="md:hidden flex justify-center py-3 bg-transparent absolute top-0 left-0 right-0 z-10">
                <div className="w-10 h-1 bg-white/80 rounded-full shadow-lg" />
              </div>

              {/* Header */}
              <div className="relative">
                <ImageWithFallback
                  src={material.imageUrl}
                  alt={material.name}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-4 right-4">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={onClose}
                    className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg"
                  >
                    <X className="w-5 h-5 text-slate-900" />
                  </motion.button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`px-3 py-1 rounded-full ${
                      material.compliance === "compliant" 
                        ? "bg-green-500" 
                        : "bg-red-500"
                    }`}>
                      <span className="text-white" style={{ fontSize: "12px", fontWeight: "600" }}>
                        {material.compliance === "compliant" ? "Compliant" : "Non-Compliant"}
                      </span>
                    </div>
                  </div>
                  <h2 className="text-white" style={{ fontSize: "24px", fontWeight: "700" }}>
                    {material.name}
                  </h2>
                  <p className="text-white/80" style={{ fontSize: "14px" }}>
                    {material.quality}
                  </p>
                </div>
              </div>

              {/* Content with bottom padding for nav bar */}
              <div className="overflow-y-auto" style={{ maxHeight: "calc(90vh - 192px - 72px)", paddingBottom: "80px" }}>
                <div className="p-5 space-y-4">
                  {/* Specifications */}
                  <div>
                    <h3 className="text-slate-900 mb-3" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Specifications
                    </h3>
                    <div className="space-y-3">
                      {specs.grade && (
                        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                          <Package className="w-4 h-4 text-slate-500 mt-0.5" />
                          <div className="flex-1">
                            <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>
                              Grade/Type
                            </div>
                            <div className="text-slate-900" style={{ fontWeight: "600" }}>
                              {specs.grade}
                            </div>
                          </div>
                        </div>
                      )}

                      {specs.supplier && (
                        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                          <MapPin className="w-4 h-4 text-slate-500 mt-0.5" />
                          <div className="flex-1">
                            <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>
                              Supplier
                            </div>
                            <div className="text-slate-900" style={{ fontWeight: "600" }}>
                              {specs.supplier}
                            </div>
                          </div>
                        </div>
                      )}

                      {specs.batchNumber && (
                        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                          <Package className="w-4 h-4 text-slate-500 mt-0.5" />
                          <div className="flex-1">
                            <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>
                              Batch Number
                            </div>
                            <div className="text-slate-900" style={{ fontWeight: "600" }}>
                              {specs.batchNumber}
                            </div>
                          </div>
                        </div>
                      )}

                      {specs.quantity && (
                        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                          <Package className="w-4 h-4 text-slate-500 mt-0.5" />
                          <div className="flex-1">
                            <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>
                              Quantity Used
                            </div>
                            <div className="text-slate-900" style={{ fontWeight: "600" }}>
                              {specs.quantity}
                            </div>
                          </div>
                        </div>
                      )}

                      {specs.testDate && (
                        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                          <Calendar className="w-4 h-4 text-slate-500 mt-0.5" />
                          <div className="flex-1">
                            <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>
                              Test Date
                            </div>
                            <div className="text-slate-900" style={{ fontWeight: "600" }}>
                              {specs.testDate}
                            </div>
                          </div>
                        </div>
                      )}

                      {specs.source && (
                        <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                          <MapPin className="w-4 h-4 text-slate-500 mt-0.5" />
                          <div className="flex-1">
                            <div className="text-slate-500 mb-0.5" style={{ fontSize: "12px" }}>
                              Source Location
                            </div>
                            <div className="text-slate-900" style={{ fontWeight: "600" }}>
                              {specs.source}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quality Status */}
                  <div>
                    <h3 className="text-slate-900 mb-3" style={{ fontSize: "16px", fontWeight: "700" }}>
                      Quality Status
                    </h3>
                    <div className={`p-4 rounded-xl border-2 ${
                      material.compliance === "compliant"
                        ? "bg-green-50 border-green-200"
                        : "bg-red-50 border-red-200"
                    }`}>
                      <div className="flex items-start gap-3">
                        {material.compliance === "compliant" ? (
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                        )}
                        <div>
                          <div className={`mb-1 ${
                            material.compliance === "compliant" ? "text-green-900" : "text-red-900"
                          }`} style={{ fontWeight: "600" }}>
                            {material.compliance === "compliant" 
                              ? "Meets Quality Standards" 
                              : "Quality Issue Detected"}
                          </div>
                          <p className={`${
                            material.compliance === "compliant" ? "text-green-700" : "text-red-700"
                          }`} style={{ fontSize: "14px" }}>
                            {material.compliance === "compliant"
                              ? "This material has passed all quality tests and meets the required specifications for the concrete mix design."
                              : "This material has failed quality tests. Please review and take corrective action."}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-5 border-t border-slate-100 bg-white">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onClose}
                  className="w-full py-3.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl transition-all font-semibold"
                >
                  Close
                </motion.button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}